import { ChangeDetectionStrategy, Component, effect, input, output, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { BpeVariableData } from "./bpe.vars.component";
import { S25ItemI } from "../../pojo/S25ItemI";
import { BpeVarsUtil } from "./s25.bpe.vars.util";
import { BpeVarsConst } from "./s25.bpe.const";
import { Doc } from "../system-settings/document-management/s25.document.const";
import { VariableWithOptions } from "../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25Util } from "../../util/s25-util";
import { AriaLabel } from "../../util/aria/aria.label";
import { S25CopyVariableComponent } from "./s25.copy.variable.component";

@Component({
    selector: "s25-ng-bpe-variable",
    template: `
        @if (state()) {
            @if (type() || types()) {
                <s25-bpe-vars-dropdown
                    class="c-margin-top--quarter"
                    [type]="type()"
                    [types]="types()"
                    [dataTypes]="dataTypes()"
                    [(value)]="state().variable"
                    (valueChange)="onVariableChange(state()); setVariable(state())"
                    [documentType]="documentType()"
                    [hasNoneItem]="hasNoneItem()"
                    [placeholder]="placeholder()"
                    [itemFilter]="itemFilter()"
                    [scrollIntoView]="scrollIntoView()"
                    [ariaLabel]="ariaLabel()"
                />
            }
            @if (state().variable?.options) {
                <div class="options">
                    @for (option of options(); track option) {
                        @if (!option.hidden) {
                            <div class="option">
                                @if (!!option.label) {
                                    <label class="option--label">{{ option.label }}</label>
                                }

                                <div class="option--value">
                                    @switch (option.type) {
                                        @case ("checkbox") {
                                            <s25-ng-checkbox
                                                [(value)]="state().options[option.id]"
                                                (valueChange)="setVariable(state())"
                                                [attr.data-id]="option.id"
                                            >
                                                {{ option.data?.label }}
                                            </s25-ng-checkbox>
                                        }
                                        @case ("dropdown") {
                                            <s25-generic-dropdown
                                                [items]="option.data.options"
                                                [(value)]="state().options[option.id]"
                                                (valueChange)="setVariable(state())"
                                            />
                                        }
                                        @case ("roleDropdown") {
                                            <s25-ng-role-dropdown
                                                class="c-margin-top--quarter"
                                                [type]="option.data.type"
                                                [(value)]="state().options[option.id]"
                                                (valueChange)="setVariable(state())"
                                            />
                                        }
                                        @case ("searchCriteriaItems") {
                                            <s25-ng-multiselect-search-criteria
                                                [type]="option.data.criterion"
                                                [modelBean]="{}"
                                                [popoverOnBody]="true"
                                                [showResults]="true"
                                                [sortResultsBy]="option.data.sortResultsBy ?? 'none'"
                                                (changed)="
                                                    state().options[option.id] = joinItemIds($event);
                                                    setVariable(state())
                                                "
                                            />
                                        }
                                        @case ("searchCriteriaItem") {
                                            <s25-ng-dropdown-search-criteria
                                                [type]="option.data.criterion"
                                                [(value)]="state().options[option.id]"
                                                [placeholder]="option.data.placeholder"
                                                (valueChange)="setVariable(state())"
                                            />
                                        }
                                        @case ("number") {
                                            <input
                                                class="c-input"
                                                type="number"
                                                [(ngModel)]="state().options[option.id]"
                                                [placeholder]="option.data.placeholder"
                                                [min]="option.data.min"
                                                [max]="option.data.max"
                                                [step]="option.data.step"
                                                (ngModelChange)="setVariable(state())"
                                            />
                                        }
                                    }
                                </div>
                            </div>
                        }
                    }
                </div>
            }
            @if (state().variable?.hasFormat && hasFormat()) {
                <label class="date-format c-margin-top--quarter">
                    <span class="selectedVarLabel">Format: </span>
                    <s25-bpe-vars-dropdown
                        [type]="isWs() ? 'wsDateFormats' : 'dateFormats'"
                        [(value)]="state().format"
                        (valueChange)="setVariable(state())"
                        [documentType]="documentType()"
                        [placeholder]="'Format'"
                    />
                </label>
            }
            @if (state().variable?.hasFormat && state().format && hasFormatExample()) {
                <div>Format Example: {{ state().format.ex }}</div>
            }

            @if (hasPost()) {
                <s25-ng-copy-variable [label]="'Variable'" [text]="state().var" />
            }
            @if (hasPre()) {
                <s25-ng-copy-variable [label]="'Pre Variable'" [text]="state().preVar" />
            }
        }
    `,
    styles: `
        .options s25-ng-checkbox {
            margin-right: 0.5em;
        }

        ::ng-deep
            s25-ng-bpe-variable
            .options
            s25-ng-checkbox[data-id="includeAll"]:has(input[type="checkbox"]:checked)
            + s25-ng-multiselect-search-criteria {
            display: none;
        }

        .options s25-ng-multiselect-search-criteria {
            display: block;
        }

        .options {
            display: grid;
            grid-template-columns: auto 1fr;
            row-gap: 0.5rem;
            column-gap: 0.5rem;
            margin-block-start: 0.5rem;
        }

        .option {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
            align-items: center;
        }

        .option--value {
            grid-column-start: 1;
            grid-column-end: 3;
        }

        .option--label + .option--value {
            grid-column-start: 2;
        }

        .option--value > * {
            width: 100%;
        }

        .date-format {
            width: 100%;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25DropdownPaginatedModule,
        S25CheckboxModule,
        S25MultiselectModule,
        FormsModule,
        S25CopyVariableComponent,
    ],
})
export class S25BpeVariableComponent {
    readonly state = input.required<BpeVariableData>();
    readonly type = input<keyof typeof BpeVarsConst.Items>();
    readonly types = input<(keyof typeof BpeVarsConst.Items)[]>();
    readonly codeMode = input(false);
    readonly documentType = input.required<Doc.TypeId>();
    readonly hasPre = input<boolean>();
    readonly hasPost = input(true);
    readonly hasFormat = input(true);
    readonly hasFormatExample = input(true);
    readonly prefix = input(true);
    readonly wrap = input(true);
    readonly hasNoneItem = input(false);
    readonly itemFilter = input<(items: DropDownItem[]) => DropDownItem[] | Promise<DropDownItem[]>>();
    readonly placeholder = input("Variable");
    readonly scrollIntoView = input<boolean>();
    readonly dataTypes = input<string[]>();
    readonly isWs = input(false);
    readonly ariaLabel = input<AriaLabel>();

    readonly variableChange = output<string>();

    readonly options = signal<VariableWithOptions["options"]>([]);

    constructor() {
        effect(() => {
            // Recompute the variable whenever the bound state or its type/document type changes.
            const state = this.state();
            this.type();
            this.documentType();
            this.setVariable(state, false);
        });
    }

    // Clear option values when variable changes
    onVariableChange(state: BpeVariableData) {
        state.options = {};
    }

    setVariable(state: BpeVariableData, updateVariable = true) {
        if (!state) return;
        this.setOptions(state);
        BpeVarsUtil.setVariable(state, this.codeMode(), this.prefix(), this.wrap());
        if (!state.variable) {
            state.var = undefined;
            state.preVar = undefined;
        }

        if (updateVariable) this.variableChange.emit(state.var!);
    }

    setOptions(state: BpeVariableData) {
        const documentType = this.documentType();
        for (const option of state?.variable?.options ?? []) {
            // Set default value
            if (!S25Util.isDefined(state.options[option.id]) && S25Util.isDefined(option.default)) {
                state.options[option.id] = option.default;
            }

            // If the option does not apply to this document type, drop its value
            const include = !option.documentTypes || option.documentTypes.includes(documentType);
            if (!include) delete state.options[option.id];
        }

        this.options.set(
            (state?.variable?.options ?? []).filter((option) => {
                if (option.documentTypes && !option.documentTypes.includes(documentType)) return false;
                // Check if options (if any) on the option itself match
                return Object.entries(option.options ?? {}).every(([key, value]) => !!state.options[key] === !!value);
            }),
        );
    }

    joinItemIds(items: S25ItemI[]) {
        return items.map((item) => item.itemId).join(",");
    }
}
